import { useEffect, useId, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import type { VoterDataGeography, VoterDataPlaceDto } from '@signalvote/shared';
import { VOTER_DATA_MAX_PLACES_PER_SEGMENT } from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import { useVoterPlaceSearch } from '@/hooks/queries';
import { campaignConfig } from '@/config/customerConfig';
import { cn } from '@/lib/utils';
import { formatCount } from './VoterDataFieldPopover';
import { nounFor } from './voterPurpose';
import type { PickedArea, PickedPlace } from './voterSavedList';

/** How long typing must pause before a search: each one ranks every district and place of the state on the server. */
export const PLACE_SEARCH_SETTLE_MS = 300;

const inputClassName =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

function useSettledText(text: string): string {
  const [settled, setSettled] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(text), PLACE_SEARCH_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [text]);
  return settled;
}

/** Why no more districts or places can be picked, once the list holds as many as it may. */
export const PLACES_FULL_NOTE = `A list can have up to ${VOTER_DATA_MAX_PLACES_PER_SEGMENT} districts and places in all. Remove one to choose another.`;

export const CANT_FIND_LABEL = 'Can’t find your district? Tell us your race and we’ll build the list.';

/** A row of the results: the whole state first, then districts and places, then the way out when none fits. */
type Row = { type: 'whole' } | { type: 'place'; place: VoterDataPlaceDto; suggested: boolean } | { type: 'help' };

/**
 * The area's one search, across every kind of district and place the state has: the whole state first, the
 * campaign's suggested district pinned under it, then what the search finds, best first, each with its kind and
 * voters. A result of the kind already picked adds to it; one of another kind replaces it, since a list has one
 * area. Picked places show as chips that remove them; a list restored with several areas shows each.
 */
export function VoterDataAreaSearch({
  state,
  areas,
  labelOf,
  kindLabelOf,
  full,
  blocked,
  suggestion,
  onWholeState,
  onPick,
  onRemovePlace,
  onRemoveArea,
}: {
  state: string;
  areas: PickedArea[];
  labelOf: (geography: VoterDataGeography) => (place: PickedPlace) => string;
  kindLabelOf: (geography: VoterDataGeography) => string;
  /** Whether the list already holds as many districts and places as it may. */
  full: boolean;
  /** Why no area can be chosen, at L2's filter cap. */
  blocked: string | null;
  /** The district the campaign's race matched, if any. */
  suggestion: VoterDataPlaceDto | null;
  onWholeState: () => void;
  onPick: (place: VoterDataPlaceDto) => void;
  onRemovePlace: (index: number, code: string) => void;
  onRemoveArea: (index: number) => void;
}) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(-1);
  const [helping, setHelping] = useState(false);
  const listId = useId();
  const q = useSettledText(text.trim());
  const search = useVoterPlaceSearch(state && q ? { state, kind: null, q, county: null } : null);
  const found = search.data?.places ?? [];
  const busy = search.failureReason instanceof ApiError && search.failureReason.status === 429 && !search.data;

  const rows: Row[] = [
    { type: 'whole' },
    ...(suggestion ? [{ type: 'place' as const, place: suggestion, suggested: true }] : []),
    ...found
      .filter((p) => !(suggestion && p.geography === suggestion.geography && p.code === suggestion.code))
      .map((place) => ({ type: 'place' as const, place, suggested: false })),
    { type: 'help' },
  ];
  useEffect(() => setActive(-1), [rows.length, text]);

  const isPicked = (place: VoterDataPlaceDto) =>
    areas.some((a) => a.geography === place.geography && a.places.some((p) => p.code === place.code));
  const disabled = (place: VoterDataPlaceDto) =>
    !isPicked(place) && ((full && areas.some((a) => a.geography === place.geography)) || (blocked !== null && areas.length === 0));
  const choose = (row: Row) => {
    if (row.type === 'whole') onWholeState();
    else if (row.type === 'help') setHelping(true);
    else if (!disabled(row.place)) onPick(row.place);
  };
  const optionId = (i: number) => `${listId}-${i}`;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i < 0 ? (step > 0 ? 0 : rows.length - 1) : (i + step + rows.length) % rows.length));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      choose(rows[active]);
    }
  };

  const rowClassName = (i: number) =>
    cn(
      'flex min-h-9 cursor-pointer items-center gap-3 rounded-md px-2.5 py-1.5 text-sm aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-selected:font-semibold aria-selected:text-primary',
      i === active && 'bg-accent',
    );

  return (
    <div className="space-y-3">
      {areas.map((area, index) => (
        <div key={area.geography} role="group" aria-label={kindLabelOf(area.geography)} className="space-y-1.5">
          {areas.length > 1 && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{index === 0 ? kindLabelOf(area.geography) : `And in ${kindLabelOf(area.geography).toLowerCase()}`}</span>
              <button
                type="button"
                aria-label={`Remove ${kindLabelOf(area.geography)} area`}
                onClick={() => onRemoveArea(index)}
                className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
          <ul aria-label="Chosen places" className="flex flex-wrap gap-1.5">
            {area.places.map((place) => {
              const label = labelOf(area.geography)(place);
              return (
                <li key={place.code} className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 py-0.5 pl-2 text-sm font-medium text-primary">
                  {label}
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    onClick={() => onRemovePlace(index, place.code)}
                    className="ml-0.5 mr-0.5 inline-flex size-5 items-center justify-center rounded opacity-60 hover:bg-primary/15 hover:opacity-100"
                  >
                    <X className="size-3" strokeWidth={3} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {full && <p className="text-sm text-muted-foreground">{PLACES_FULL_NOTE}</p>}
      {blocked && <p className="text-sm text-muted-foreground">{blocked}</p>}
      <div className="space-y-1.5">
        <input
          type="text"
          role="combobox"
          aria-label="Search districts and places"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder="Search a district, city, county or school board"
          disabled={!state}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          className={inputClassName}
        />
        <div id={listId} role="listbox" aria-label="Areas" aria-multiselectable="true" className="max-h-72 overflow-y-auto">
          {rows.map((row, i) => {
            if (row.type === 'whole') {
              return (
                <div key="whole" id={optionId(i)} role="option" aria-selected={areas.length === 0} onMouseMove={() => setActive(i)} onClick={() => choose(row)} className={rowClassName(i)}>
                  <span className="flex-1">Whole state</span>
                </div>
              );
            }
            if (row.type === 'help') {
              return (
                <div key="help" id={optionId(i)} role="option" aria-selected={false} onMouseMove={() => setActive(i)} onClick={() => choose(row)} className={cn(rowClassName(i), 'text-muted-foreground')}>
                  <span className="flex-1">{CANT_FIND_LABEL}</span>
                </div>
              );
            }
            const { place, suggested } = row;
            const voters = `${formatCount(place.voters)} ${nounFor(place.voters, 'voter')}`;
            return (
              <div
                key={`${place.geography}:${place.code}`}
                id={optionId(i)}
                role="option"
                aria-selected={isPicked(place)}
                aria-disabled={disabled(place) || undefined}
                aria-label={place.label}
                aria-description={`${place.kindLabel}, ${voters}${suggested ? ', suggested for your race' : ''}`}
                onMouseMove={() => setActive(i)}
                onClick={() => choose(row)}
                className={rowClassName(i)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block">
                    {place.label}
                    {suggested && <span className="ml-1.5 text-xs font-medium text-primary">Your race</span>}
                  </span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    <span className="rounded border px-1 py-px">{place.kindLabel}</span>{' '}
                    <span className="tabular-nums">{voters}</span>
                  </span>
                </span>
              </div>
            );
          })}
        </div>
        {helping && (
          <p role="status" className="text-sm text-muted-foreground">
            {campaignConfig.supportEmail
              ? `Email ${campaignConfig.supportEmail} with your race and state, and we’ll build the list for you.`
              : 'Contact support with your race and state, and we’ll build the list for you.'}
          </p>
        )}
        <SearchNote search={search} busy={busy} q={q} shown={found.length} />
      </div>
    </div>
  );
}

function SearchNote({ search, busy, q, shown }: { search: ReturnType<typeof useVoterPlaceSearch>; busy: boolean; q: string; shown: number }) {
  const note = 'text-sm text-muted-foreground';
  if (busy) {
    const seconds = search.failureReason instanceof ApiError ? search.failureReason.retryAfterSeconds : null;
    return (
      <p role="status" className={note}>
        The voter file is busy. Trying again {seconds ? `in about ${seconds} s` : 'in a few minutes'}.
      </p>
    );
  }
  if (search.error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn’t search places right now. Please try again shortly.
      </p>
    );
  }
  if (!search.data) return search.isFetching ? <p className={note}>Searching…</p> : null;
  if (shown === 0 && q) return <p className={note}>Nothing matches “{q}”. Try the name of the city, county or district.</p>;
  return null;
}
